import { colors, hexToRgbA } from '@karnott/colors';
import { formatArea, formatDistance, formatDuration, formatVolume } from '../utils';
import { Values } from '../utils/types';
import { EQUIPMENT_CONSTANTS } from './equipmentConstants';

const BASE_DIAGRAM_COLORS = [
  colors('bleen'),
  colors('green'),
  colors('orange'),
  colors('bleen', 'light'),
  colors('green', 'light'),
  colors('orange', 'light'),
  colors('bleen', 'dark'),
  colors('green', 'dark'),
  colors('orange', 'dark'),
  colors('sepia'),
  colors('cyan'),
  colors('red'),
  colors('yellow'),
  colors('sepia', 'light'),
  colors('cyan', 'light'),
  colors('red', 'light'),
  colors('yellow', 'light'),
  colors('sepia', 'dark'),
  colors('cyan', 'dark'),
  colors('red', 'dark'),
  colors('yellow', 'dark'),
];

function buildDiagramColors() {
  return BASE_DIAGRAM_COLORS.concat(BASE_DIAGRAM_COLORS.map((color) => hexToRgbA(color, 0.7)).filter(Boolean)).concat(
    BASE_DIAGRAM_COLORS.map((color) => hexToRgbA(color, 0.4)).filter(Boolean),
  );
}

export const REPORTS_CONSTANTS = {
  SESSIONS: {
    device_session_status: {
      active: 'active',
      computed: 'computed',
      debug: 'debug',
      ended: 'ended',
    },
  },
  DIMENSIONS: {
    equipments_report: [
      'totalArea',
      'totalDistance',
      'totalInParcelsDistance',
      'totalOutOfParcelsDistance',
      'totalDuration',
      'totalMotorDuration',
      'totalMovementDuration',
      'totalInParcelsMotorDuration',
      'totalOutOfParcelsMotorDuration',
      'totalUnit',
      'totalFuel',
      'totalInParcelsFuel',
      'totalOutOfParcelsFuel',
    ],
    METHOD_TO_FORMAT: {
      totalArea: formatArea,
      totalDistance: formatDistance,
      totalDuration: formatDuration,
      totalInParcelsDistance: formatDistance,
      totalInParcelsMotorDuration: formatDuration,
      totalMotorDuration: formatDuration,
      totalMovementDuration: formatDuration,
      totalOutOfParcelsDistance: formatDistance,
      totalOutOfParcelsMotorDuration: formatDuration,
      totalFuel: formatVolume,
      totalInParcelsFuel: formatVolume,
      totalOutOfParcelsFuel: formatVolume,
      numberOfSession: (nb: number) => nb,
      totalUnit: (tu: number) => tu,
    },
    UNIT_TO_USE: {
      totalArea: 'area',
      totalDistance: 'distance',
      totalDuration: 'time',
      totalInParcelsDistance: 'distance',
      totalInParcelsMotorDuration: 'time',
      totalMotorDuration: 'time',
      totalMovementDuration: 'time',
      totalOutOfParcelsDistance: 'distance',
      totalOutOfParcelsMotorDuration: 'time',
      totalFuel: 'volume',
      totalInParcelsFuel: 'volume',
      totalOutOfParcelsFuel: 'volume',
    },
    CORRESPONDING_BILLING_METHOD: {
      [EQUIPMENT_CONSTANTS.BILLING_METHODS.AREA]: 'totalArea',
      [EQUIPMENT_CONSTANTS.BILLING_METHODS.TIME]: 'totalMotorDuration',
      [EQUIPMENT_CONSTANTS.BILLING_METHODS.TIME_IN_FIELD]: 'totalInParcelsMotorDuration',
      [EQUIPMENT_CONSTANTS.BILLING_METHODS.TIME_OUT_FIELD]: 'totalOutOfParcelsMotorDuration',
      [EQUIPMENT_CONSTANTS.BILLING_METHODS.TIME_MOVEMENT]: 'totalMovementDuration',
      [EQUIPMENT_CONSTANTS.BILLING_METHODS.DURATION]: 'totalDuration',
      [EQUIPMENT_CONSTANTS.BILLING_METHODS.DISTANCE]: 'totalDistance',
      [EQUIPMENT_CONSTANTS.BILLING_METHODS.DISTANCE_IN_FIELD]: 'totalInParcelsDistance',
      [EQUIPMENT_CONSTANTS.BILLING_METHODS.DISTANCE_OUT_FIELD]: 'totalOutOfParcelsDistance',
      [EQUIPMENT_CONSTANTS.BILLING_METHODS.UNIT]: 'totalUnit',
      [EQUIPMENT_CONSTANTS.BILLING_METHODS.FUEL]: 'totalFuel',
      [EQUIPMENT_CONSTANTS.BILLING_METHODS.FUEL_IN_FIELD]: 'totalInParcelsFuel',
      [EQUIPMENT_CONSTANTS.BILLING_METHODS.FUEL_OUT_FIELD]: 'totalOutOfParcelsFuel',
    },
    PARCEL_CORRESPONDING_BILLING_METHOD: {
      [EQUIPMENT_CONSTANTS.BILLING_METHODS.AREA]: 'totalArea',
      [EQUIPMENT_CONSTANTS.BILLING_METHODS.TIME]: 'totalMotorDuration',
      [EQUIPMENT_CONSTANTS.BILLING_METHODS.TIME_IN_FIELD]: 'totalMotorDuration',
      [EQUIPMENT_CONSTANTS.BILLING_METHODS.TIME_OUT_FIELD]: 'zero',
      [EQUIPMENT_CONSTANTS.BILLING_METHODS.TIME_MOVEMENT]: 'totalMovementDuration',
      [EQUIPMENT_CONSTANTS.BILLING_METHODS.DURATION]: 'totalDuration',
      [EQUIPMENT_CONSTANTS.BILLING_METHODS.DISTANCE]: 'totalDistance',
      [EQUIPMENT_CONSTANTS.BILLING_METHODS.DISTANCE_IN_FIELD]: 'totalDistance',
      [EQUIPMENT_CONSTANTS.BILLING_METHODS.DISTANCE_OUT_FIELD]: 'zero',
      [EQUIPMENT_CONSTANTS.BILLING_METHODS.UNIT]: 'totalUnit',
      [EQUIPMENT_CONSTANTS.BILLING_METHODS.FUEL]: 'totalFuel',
      [EQUIPMENT_CONSTANTS.BILLING_METHODS.FUEL_IN_FIELD]: 'totalFuel',
      [EQUIPMENT_CONSTANTS.BILLING_METHODS.FUEL_OUT_FIELD]: 'zero',
    },
  },
  DIAGRAM_COLORS: buildDiagramColors(),
  DIMENSION_TABS: {
    TIME: 'Commons.motor_duration',
    DISTANCE: 'Commons.traveled_distance',
    AREA: 'Commons.worked_area',
  },
} as const;
export type DeviceSessionStatus = Values<typeof REPORTS_CONSTANTS.SESSIONS.device_session_status>;

export const REPORT_EQUIPMENT_SORTING = {
  ALPHABETICALLY: 'alphabetically',
  TYPE: 'type',
} as const;

export type ReportEquipmentSort = Values<typeof REPORT_EQUIPMENT_SORTING>;
